import { useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "../../lib";
import { ActivityRecord, getActivities, GetActivitiesParams } from "../activities";
import { PaginationMeta } from "../../lib/bufferedPagination";

type UseActivitiesState = {
  activities: ActivityRecord[];
  meta?: PaginationMeta;
  isLoading: boolean;
  error: string | null;
};

const activitiesCache = new Map<string, { items: ActivityRecord[]; meta?: PaginationMeta }>();

export const useActivities = (params: GetActivitiesParams) => {
  const [state, setState] = useState<UseActivitiesState>({
    activities: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);
  const requestKey = JSON.stringify(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestParams = useMemo(() => params, [requestKey]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = activitiesCache.get(requestKey);
      setState((prev) => ({
        activities: cached?.items ?? prev.activities,
        meta: cached?.meta ?? prev.meta,
        isLoading: cached ? false : true,
        error: null,
      }));
      try {
        const activitiesResult = await getActivities(requestParams);
        if (!isMounted) return;
        activitiesCache.set(requestKey, activitiesResult);
        setState({
          activities: activitiesResult.items,
          meta: activitiesResult.meta,
          isLoading: false,
          error: null,
        });

        const nextParams = {
          ...requestParams,
          page: (requestParams.page ?? 1) + 1,
        };
        const nextKey = JSON.stringify(nextParams);
        if (!activitiesCache.has(nextKey)) {
          getActivities(nextParams)
            .then((nextResult) => {
              activitiesCache.set(nextKey, nextResult);
            })
            .catch(() => {});
        }
      } catch (error) {
        if (!isMounted) return;
        const message = getApiErrorMessage(error, "Unable to load activity logs");
        setState({
          activities: [],
          meta: undefined,
          isLoading: false,
          error: message,
        });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [refreshCount, requestKey, requestParams]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
